import { Add, Delete, ExpandMore as ExpandMoreIcon } from '@tunasong/icons'
import type { GearCategory, GearRequirement, Rider } from '@tunasong/schemas'
import { gearCategoryValues } from '@tunasong/schemas'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import {Accordion, AccordionDetails, AccordionSummary, Button, IconButton, Chip, Typography, FormControl, InputLabel, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField} from '@tunasong/ui-lib'
import styles from './edit-form.module.css'

export interface EditFormProps {
  element: Rider
  onChange: (partial: Partial<Rider>) => void
}

/** Category labels for display */
const categoryLabels: Record<GearCategory, string> = {
  microphone: 'Mic',
  monitor: 'Mon',
  stand: 'Stand',
  instrument: 'Inst',
  backline: 'Backline',
  accessory: 'Acc',
  other: 'Other',
}

/** Full category labels for dropdowns */
const categoryFullLabels: Record<GearCategory, string> = {
  microphone: 'Microphone',
  monitor: 'Monitor',
  stand: 'Stand',
  instrument: 'Instrument',
  backline: 'Backline',
  accessory: 'Accessory',
  other: 'Other',
}

const defaultGearRequirement: GearRequirement = {
  category: 'microphone',
  name: '',
  quantity: 1,
  providedByArtist: false,
}

export const EditForm: FC<EditFormProps> = ({ element, onChange }) => {
  const [newBacklineItem, setNewBacklineItem] = useState<GearRequirement>(defaultGearRequirement)
  const [newDrink, setNewDrink] = useState('')

  const updateField = useCallback(
    <K extends keyof Rider>(field: K, value: Rider[K]) => {
      onChange({ [field]: value })
    },
    [onChange]
  )

  const updateMember = useCallback(
    (index: number, field: keyof Rider['members'][0], value: unknown) => {
      const members = [...(element.members ?? [])]
      members[index] = { ...members[index], [field]: value }
      onChange({ members })
    },
    [element.members, onChange]
  )

  const removeMember = useCallback(
    (index: number) => {
      const members = element.members?.filter((_, i) => i !== index) ?? []
      onChange({ members })
    },
    [element.members, onChange]
  )

  const updateMonitors = useCallback(
    (field: 'wedges' | 'iemPacks' | 'mixes', value: number) => {
      const shared = {
        ...element.shared,
        monitors: {
          ...element.shared?.monitors,
          [field]: Number.isNaN(value) ? undefined : value,
        },
      }
      onChange({ shared })
    },
    [element.shared, onChange]
  )

  const addBacklineItem = useCallback(() => {
    if (!newBacklineItem.name.trim()) {
      return
    }
    const backline = [...(element.shared?.backline ?? []), newBacklineItem]
    const shared = { ...element.shared, backline }
    onChange({ shared })
    setNewBacklineItem(defaultGearRequirement)
  }, [element.shared, newBacklineItem, onChange])

  const removeBacklineItem = useCallback(
    (index: number) => {
      const backline = element.shared?.backline?.filter((_, i) => i !== index) ?? []
      const shared = { ...element.shared, backline }
      onChange({ shared })
    },
    [element.shared, onChange]
  )

  const updateStageSize = useCallback(
    (field: 'width' | 'depth', value: number) => {
      const shared = {
        ...element.shared,
        stageSize: {
          width: element.shared?.stageSize?.width ?? 0,
          depth: element.shared?.stageSize?.depth ?? 0,
          [field]: value,
        },
      }
      onChange({ shared })
    },
    [element.shared, onChange]
  )

  const updateHospitality = useCallback(
    (field: keyof NonNullable<Rider['hospitality']>, value: unknown) => {
      const hospitality = { ...element.hospitality, [field]: value }
      onChange({ hospitality })
    },
    [element.hospitality, onChange]
  )

  return (
    <div className={styles.root}>
      {/* Basic Info */}
      <div>
        <Typography variant="h6" gutterBottom>
          Basic Information
        </Typography>
        <div className={styles.fieldColumn}>
          <TextField
            fullWidth
            label="Event Name"
            value={element.eventName ?? ''}
            onChange={e => updateField('eventName', e.target.value || undefined)}
          />
          <TextField
            fullWidth
            label="Venue"
            value={element.venue ?? ''}
            onChange={e => updateField('venue', e.target.value || undefined)}
          />
          <TextField
            fullWidth
            label="Event Date"
            type="date"
            value={element.eventDate ?? ''}
            onChange={e => updateField('eventDate', e.target.value || undefined)}
          />
        </div>
      </div>

      {/* Members */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Input List ({element.members?.length ?? 0} members)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {element.members?.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ch</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Requirements</TableCell>
                  <TableCell>Monitor Mix</TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {element.members.map((member, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={member.channelNumber ?? ''}
                        onChange={e => {
                          const v = parseInt(e.target.value, 10)
                          updateMember(index, 'channelNumber', Number.isNaN(v) ? undefined : v)
                        }}
                        className={styles.channelInput}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {member.userId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={member.role ?? ''}
                        onChange={e => updateMember(index, 'role', e.target.value || undefined)}
                        className={styles.roleInput}
                      />
                    </TableCell>
                    <TableCell>
                      {member.requirements.length > 0 ? (
                        <div>
                          {member.requirements.map((req, i) => (
                            <div key={i} className={styles.requirementItem}>
                              <div className={styles.requirementRow}>
                                <Chip
                                  size="small"
                                  label={categoryLabels[req.category] ?? req.category}
                                  color="primary"
                                  variant="outlined"
                                  className={styles.chipMinWidth}
                                />
                                <Typography variant="body2">
                                  {req.quantity > 1 ? `${req.quantity}x ` : ''}
                                  <strong>{req.name}</strong>
                                </Typography>
                                {req.providedByArtist ? (
                                  <Chip size="small" label="Artist" variant="outlined" color="success" />
                                ) : null}
                              </div>
                              {req.notes ? (
                                <Typography variant="caption" color="textSecondary" className={styles.noteMargin}>
                                  {req.notes}
                                </Typography>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          No requirements (sync from profile)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={member.monitorMix ?? ''}
                        onChange={e => {
                          const v = parseInt(e.target.value, 10)
                          updateMember(index, 'monitorMix', Number.isNaN(v) ? undefined : v)
                        }}
                        className={styles.monitorInput}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeMember(index)}>
                        <Delete size={16} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography color="textSecondary">No members yet</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Monitors */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Monitor Requirements</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.rowGap16}>
            <TextField
              label="Wedge Monitors"
              type="number"
              value={element.shared?.monitors?.wedges ?? ''}
              onChange={e => updateMonitors('wedges', parseInt(e.target.value, 10))}
              min={0}
            />
            <TextField
              label="IEM Packs"
              type="number"
              value={element.shared?.monitors?.iemPacks ?? ''}
              onChange={e => updateMonitors('iemPacks', parseInt(e.target.value, 10))}
              min={0}
            />
            <TextField
              label="Monitor Mixes"
              type="number"
              value={element.shared?.monitors?.mixes ?? ''}
              onChange={e => updateMonitors('mixes', parseInt(e.target.value, 10))}
              min={0}
            />
          </div>
        </AccordionDetails>
      </Accordion>

      {/* Backline */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Backline</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.fieldColumn}>
            {element.shared?.backline?.length ? (
              <div className={styles.backlineChips}>
                {element.shared.backline.map((req, index) => (
                  <Chip
                    key={index}
                    label={`${req.quantity > 1 ? `${req.quantity}x ` : ''}${req.name}`}
                    onDelete={() => removeBacklineItem(index)}
                    variant={req.providedByArtist ? 'outlined' : 'filled'}
                  />
                ))}
              </div>
            ) : (
              <Typography color="textSecondary">No backline items</Typography>
            )}

            <div className={styles.addRow}>
              <FormControl className={styles.categorySelect}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newBacklineItem.category}
                  onChange={e => setNewBacklineItem({ ...newBacklineItem, category: e.target.value as GearCategory })}
                >
                  {gearCategoryValues.map(cat => (
                    <option key={cat} value={cat}>
                      {categoryFullLabels[cat]}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Item name"
                value={newBacklineItem.name}
                onChange={e => setNewBacklineItem({ ...newBacklineItem, name: e.target.value })}
                className={styles.flex1}
              />
              <TextField
                size="small"
                label="Qty"
                type="number"
                value={newBacklineItem.quantity}
                onChange={e => setNewBacklineItem({ ...newBacklineItem, quantity: parseInt(e.target.value, 10) || 1 })}
                className={styles.qtyInput}
                min={1}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={addBacklineItem}
                disabled={!newBacklineItem.name.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      {/* Stage Requirements */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Stage Requirements</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.fieldColumn}>
            <div>
              <Typography variant="subtitle2" gutterBottom>
                Minimum Stage Size
              </Typography>
              <div className={styles.rowGap16}>
                <TextField
                  label="Width (m)"
                  type="number"
                  value={element.shared?.stageSize?.width ?? ''}
                  onChange={e => updateStageSize('width', parseFloat(e.target.value) || 0)}
                  min={0}
                  step={0.5}
                />
                <TextField
                  label="Depth (m)"
                  type="number"
                  value={element.shared?.stageSize?.depth ?? ''}
                  onChange={e => updateStageSize('depth', parseFloat(e.target.value) || 0)}
                  min={0}
                  step={0.5}
                />
              </div>
            </div>
            <TextField
              fullWidth
              label="Power Requirements"
              value={element.shared?.power ?? ''}
              onChange={e => {
                const shared = { ...element.shared, power: e.target.value || undefined }
                onChange({ shared })
              }}
              placeholder="e.g., 4x 16A circuits"
            />
            <TextField
              fullWidth
              label="PA System Notes"
              value={element.shared?.paSystem ?? ''}
              onChange={e => {
                const shared = { ...element.shared, paSystem: e.target.value || undefined }
                onChange({ shared })
              }}
              placeholder="e.g., Line array preferred"
            />
          </div>
        </AccordionDetails>
      </Accordion>

      {/* Hospitality */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Hospitality</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.fieldColumn}>
            <TextField
              label="Meals"
              type="number"
              value={element.hospitality?.meals ?? ''}
              onChange={e => {
                const v = parseInt(e.target.value, 10)
                updateHospitality('meals', Number.isNaN(v) ? undefined : v)
              }}
              min={0}
            />
            <TextField
              label="Parking Spaces"
              type="number"
              value={element.hospitality?.parkingSpaces ?? ''}
              onChange={e => {
                const v = parseInt(e.target.value, 10)
                updateHospitality('parkingSpaces', Number.isNaN(v) ? undefined : v)
              }}
              min={0}
            />
            <div>
              <Typography variant="subtitle2" gutterBottom>
                Drinks
              </Typography>
              {element.hospitality?.drinks?.length ? (
                <div className={styles.drinksChips}>
                  {element.hospitality.drinks.map((drink, index) => (
                    <Chip
                      key={index}
                      label={drink}
                      onDelete={() => {
                        const drinks = element.hospitality?.drinks?.filter((_, i) => i !== index)
                        updateHospitality('drinks', drinks?.length ? drinks : undefined)
                      }}
                    />
                  ))}
                </div>
              ) : (
                <Typography color="textSecondary" className={styles.noDrinksText}>
                  No drinks added
                </Typography>
              )}
              <div className={styles.addRow}>
                <TextField
                  size="small"
                  label="Add drink"
                  value={newDrink}
                  onChange={e => setNewDrink(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newDrink.trim()) {
                      e.preventDefault()
                      const drinks = [...(element.hospitality?.drinks ?? []), newDrink.trim()]
                      updateHospitality('drinks', drinks)
                      setNewDrink('')
                    }
                  }}
                  placeholder="Water, Beer, Coffee..."
                  className={styles.flex1}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => {
                    if (newDrink.trim()) {
                      const drinks = [...(element.hospitality?.drinks ?? []), newDrink.trim()]
                      updateHospitality('drinks', drinks)
                      setNewDrink('')
                    }
                  }}
                  disabled={!newDrink.trim()}
                >
                  Add
                </Button>
              </div>
            </div>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Hospitality Notes"
              value={element.hospitality?.notes ?? ''}
              onChange={e => updateHospitality('notes', e.target.value || undefined)}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

export default EditForm
